import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { NEWS, NEWS_CATEGORY_LABELS, type NewsCategory } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";

const PAGE_SIZE = 5;

const FILTERS: { label: string; value: NewsCategory | "all" }[] = [
  { label: "全部", value: "all" },
  { label: "行业新闻", value: "news" },
  { label: "片场花絮", value: "gossip" },
  { label: "深度评论", value: "review" },
];

export default function NewsListPage() {
  const [category, setCategory] = useState<NewsCategory | "all">("all");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: "最新影视资讯 - WWW.17C影视平台",
    description:
      "WWW.17C 最新影视资讯栏目，汇集行业新闻、片场花絮与深度评论，第一时间了解电影、电视剧与综艺的最新动态。",
    keywords: "影视资讯,行业新闻,片场花絮,影视评论,WWW.17C",
  });

  useEffect(() => {
    setPage(1);
  }, [category, keyword]);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return NEWS.filter((item) => {
      const matchCategory = category === "all" || item.category === category;
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw);
      return matchCategory && matchKeyword;
    }).sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
  }, [category, keyword]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const reset = () => {
    setCategory("all");
    setKeyword("");
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />

      <div className="mt-4 rounded-lg bg-primary-950 px-6 py-8 md:px-10 md:py-10">
        <h1 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">最新影视资讯</h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-background-300">
          行业新闻、片场花絮与深度评论一网打尽。在这里了解影视圈正在发生的事，掌握最新动态与趋势。
        </p>
      </div>

      {/* 筛选与搜索 */}
      <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="inline-flex w-fit items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setCategory(f.value)}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                category === f.value
                  ? "bg-primary-600 text-background-50"
                  : "text-foreground-600 hover:text-primary-600"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex h-10 w-full items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 md:w-72">
          <i className="ri-search-line text-sm text-foreground-400"></i>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="在资讯中搜索关键词"
            className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
          />
        </div>
      </div>

      {/* 列表 */}
      <div className="mt-6">
        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-newspaper-line"
            title="没有找到相关资讯"
            description="换个关键词，或重置筛选条件试试。"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <div className="space-y-4">
            {pageItems.map((item) => (
              <article
                key={item.id}
                className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-secondary-300 sm:flex-row"
              >
                <Link
                  to={`/news/${item.id}`}
                  className="h-40 w-full shrink-0 overflow-hidden rounded-md bg-background-200 sm:h-32 sm:w-52"
                >
                  <img
                    src={item.cover}
                    alt={item.title}
                    title={`${item.title} - WWW.17C`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 hover:scale-105"
                  />
                </Link>
                <div className="flex min-w-0 flex-1 flex-col">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                    <span className="rounded-full bg-accent-100 px-2.5 py-0.5 font-medium text-accent-700">
                      {NEWS_CATEGORY_LABELS[item.category]}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {item.publishedAt}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-newspaper-line"></i>
                      {item.source}
                    </span>
                  </div>
                  <h2 className="mt-2 font-heading text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                    <Link to={`/news/${item.id}`} className="transition-colors hover:text-primary-600">
                      {item.title}
                    </Link>
                  </h2>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{item.summary}</p>
                  <Link
                    to={`/news/${item.id}`}
                    className="mt-auto flex items-center gap-1 pt-3 text-sm font-medium text-secondary-600 transition-colors hover:text-primary-600"
                  >
                    阅读全文
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}